import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAudioRecording,
  type AudioRecordingResult,
} from '@/hooks/useAudioRecording'
import { useSequencerStore } from '@/stores/sequencerStore'
import { useMidiStore } from '@/stores/midiStore'
import { useUIStore } from '@/stores/uiStore'
import type { PadContent } from '@/stores/sequencerStore'

/**
 * Orchestrates the "record straight into a pad" flow. Wraps
 * `useAudioRecording` (which owns capture + IndexedDB save) with the
 * sequencer-side assignment plumbing so one tap captures, saves,
 * and wires the sample to the pad in a single gesture.
 *
 * Mutual exclusion is enforced via `uiStore.recordingPadIndex` — only
 * one pad can be armed at once. Buttons for other pads read that
 * value and disable while recording is in flight; the badge /
 * pulsing ring rendered by PadGrid reads the same value.
 *
 * Error paths:
 *   - `start()` throws when another pad is already recording so the
 *     caller can keep its UI in sync (prevents the rare race where
 *     two clicks fire in the same frame).
 *   - `start()` rejects when the underlying `useAudioRecording.start`
 *     does (permission denied, unsupported runtime). We surface the
 *     rejection verbatim; the button-level UI shows the hook's
 *     `error` field.
 *
 * The pad's existing MIDI-note binding (`padNoteBindings[padIndex]`)
 * is preserved as `PadContent.midiNote` so the pad stays triggerable
 * from the same external key the user had already mapped.
 */

interface UseRecordToPadResult {
  /** True when THIS pad is the currently armed pad (even before
   *  audio actually starts, so the UI can pulse immediately). */
  isRecording: boolean
  /** Live elapsed seconds for this pad's take. */
  elapsedSec: number
  /** True while THIS pad's count-in pre-roll is playing (before
   *  real capture begins). Mirrors `useAudioRecording.countingIn`
   *  gated on `isRecording` so neighbour pads don't light up. */
  countingIn: boolean
  /** Seconds remaining in this pad's count-in. 0 when not counting
   *  in. Mirrors `useAudioRecording.countInRemainingSec`. */
  countInRemainingSec: number
  /** Last error from start / stop; cleared on the next start. */
  error: string | null
  /** Whether the runtime supports recording at all. Mirrors
   *  `useAudioRecording.supported`. */
  supported: boolean
  /** True when ANY pad (not necessarily this one) is currently
   *  recording. Used by the button to disable itself while a
   *  neighbour is armed. */
  anotherPadRecording: boolean
  /** Arm this pad + begin capturing. */
  start: () => Promise<void>
  /** Finalise, save to IndexedDB, assign the new sample to this
   *  pad, and clear the armed state. Rejects when not the armed pad. */
  stop: () => Promise<AudioRecordingResult>
  /** Abandon the take without assigning. Idempotent. */
  cancel: () => void
}

export function useRecordToPad(padIndex: number): UseRecordToPadResult {
  const recordingPadIndex = useUIStore(s => s.recordingPadIndex)
  const setRecordingPadIndex = useUIStore(s => s.setRecordingPadIndex)
  const setRecordingCountInState = useUIStore(s => s.setRecordingCountInState)
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const addProjectSample = useSequencerStore(s => s.addProjectSample)
  const padMidiNote = useMidiStore(s => s.padNoteBindings[padIndex])
  const qc = useQueryClient()

  const isRecording = recordingPadIndex === padIndex
  const anotherPadRecording =
    recordingPadIndex !== null && recordingPadIndex !== padIndex

  const recording = useAudioRecording({
    onRecordingComplete: result => {
      // Build a sample-kind PadContent that points at the freshly
      // saved local sample. s3Key is empty because local samples live
      // only in IndexedDB (see rowToSample); the playback path
      // handles the empty string by falling through to playUrl, which
      // is a blob: URL for local rows.
      const content: PadContent = {
        kind: 'sample',
        sampleId: result.sampleId,
        s3Key: '',
        name: result.name,
        midiNote: padMidiNote ?? 60,
      }
      setPadContent(padIndex, content)
      // Mirror the PadEditor assign wrapper — register with the
      // active project so the slicer's project-scoped view sees it.
      addProjectSample(result.sampleId)
      // Invalidate the local-samples query so PadEditor's sample
      // picker + the slicer library refresh without a manual reload.
      qc.invalidateQueries({ queryKey: ['localSamples'] })
    },
  })

  // Clear the armed-pad flag when recording actually winds down —
  // either through a successful stop, an error, or a cancel. Watching
  // `recording.isRecording` alone is NOT enough: there's a gap
  // between `setRecordingPadIndex(padIndex)` and `recording.start()`
  // flipping isRecording → true (worst case ~2s for a 1-bar count-in
  // at 120 BPM). During that gap the effect would see
  // `isRecording=false` + the correct pad index and prematurely
  // clear the armed flag, which caused "ARM + count-in doesn't
  // actually record" in testing — stop() later threw because the
  // pad index didn't match.
  //
  // Fix: track whether we've OBSERVED `isRecording` flip true at
  // any point. Only clear the armed flag on the true → false
  // transition, never before the first true.
  const wasRecordingRef = useRef(false)
  useEffect(() => {
    if (recording.isRecording) {
      wasRecordingRef.current = true
      return
    }
    if (wasRecordingRef.current && recordingPadIndex === padIndex) {
      wasRecordingRef.current = false
      setRecordingPadIndex(null)
    }
  }, [recording.isRecording, recordingPadIndex, padIndex, setRecordingPadIndex])

  // Mirror the count-in state into uiStore so global UI (transport
  // ARM button, etc.) can render a countdown without needing to know
  // which pad is armed. Only the armed pad pushes — every other
  // instance of this hook sees `isRecording === false` and skips.
  const isArmedForThisPad = recordingPadIndex === padIndex
  useEffect(() => {
    if (!isArmedForThisPad) return
    setRecordingCountInState(
      recording.countingIn,
      recording.countInRemainingSec,
    )
    return () => {
      // Clear on teardown so the flag doesn't stick after this pad
      // finishes. Safe even if another pad immediately arms — its
      // own effect will overwrite with its own state on next frame.
      setRecordingCountInState(false, 0)
    }
  }, [
    isArmedForThisPad,
    recording.countingIn,
    recording.countInRemainingSec,
    setRecordingCountInState,
  ])

  const start = useCallback(async () => {
    if (recordingPadIndex !== null && recordingPadIndex !== padIndex) {
      throw new Error(
        `Pad ${recordingPadIndex + 1} is already recording. Stop it before arming another pad.`,
      )
    }
    setRecordingPadIndex(padIndex)
    try {
      // Read the user's count-in preference at start-time so the
      // value reflects the latest dropdown choice (even if the hook
      // was instantiated before the user changed it).
      const countInBars = useUIStore.getState().recordCountInBars
      await recording.start({ countInBars })
    } catch (err) {
      // If the hook-level start fails (permission denied etc.), clear
      // the armed flag so another pad can try. The effect above also
      // clears once isRecording settles back to false, but this
      // double-clear is idempotent and keeps the UI responsive on
      // permission-denied paths where the hook never flips
      // isRecording to true in the first place.
      setRecordingPadIndex(null)
      throw err
    }
  }, [recording, padIndex, recordingPadIndex, setRecordingPadIndex])

  const stop = useCallback(async (): Promise<AudioRecordingResult> => {
    if (recordingPadIndex !== padIndex) {
      throw new Error('This pad is not currently recording')
    }
    return recording.stop()
  }, [recording, padIndex, recordingPadIndex])

  const cancel = useCallback(() => {
    if (recordingPadIndex !== padIndex) return
    recording.cancel()
  }, [recording, padIndex, recordingPadIndex])

  return {
    isRecording,
    elapsedSec: isRecording ? recording.elapsedSec : 0,
    countingIn: isRecording ? recording.countingIn : false,
    countInRemainingSec: isRecording ? recording.countInRemainingSec : 0,
    error: recording.error,
    supported: recording.supported,
    anotherPadRecording,
    start,
    stop,
    cancel,
  }
}
